import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { RenewalTimeliness as RenewalTimelinessType } from "../../../types/contractDashboard";
import styles from "./renewalTimeliness.module.scss";

interface RenewalTimelinessProps {
  data: RenewalTimelinessType[];
}

const RenewalTimeliness: React.FC<RenewalTimelinessProps> = ({ data }) => {
  // Memoize the formatted chart data
  const chartData = useMemo(() => {
    return data.map((item) => ({
      period: item.period,
      onTime: item.onTimePct,
      late: item.latePct,
    }));
  }, [data]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.tooltip}>
          <Typography variant="body2" className={styles.tooltipPeriod}>
            {label}
          </Typography>
          <Box className={styles.tooltipContent}>
            <Box className={styles.tooltipRow}>
              <Box className={styles.tooltipDot} style={{ background: "#10b981" }} />
              <Typography variant="body2" className={styles.tooltipLabel}>
                On Time:
              </Typography>
              <Typography variant="body2" className={styles.tooltipValue}>
                {payload[0].value}%
              </Typography>
            </Box>
            <Box className={styles.tooltipRow}>
              <Box className={styles.tooltipDot} style={{ background: "#ef4444" }} />
              <Typography variant="body2" className={styles.tooltipLabel}>
                Late:
              </Typography>
              <Typography variant="body2" className={styles.tooltipValue}>
                {payload[1].value}%
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = () => {
    return (
      <Box className={styles.legend}>
        <Box className={styles.legendItem}>
          <Box className={styles.legendDot} style={{ background: "#10b981" }} />
          <Typography variant="body2" className={styles.legendLabel}>
            On Time
          </Typography>
        </Box>
        <Box className={styles.legendItem}>
          <Box className={styles.legendDot} style={{ background: "#ef4444" }} />
          <Typography variant="body2" className={styles.legendLabel}>
            Late
          </Typography>
        </Box>
      </Box>
    );
  };

  // Truncate long period names for Y-axis
  const formatYAxis = (value: string) => {
    return value.length > 12 ? `${value.substring(0, 12)}...` : value;
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Renewal & Obligation Timeliness
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            % of contracts reviewed/renewed on time.
          </Typography>
        </Box>
        <Box className={styles.emptyState}>
          <Typography variant="body2" className={styles.emptyText}>
            No renewal timeliness data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Renewal & Obligation Timeliness
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          % of contracts reviewed/renewed on time.
        </Typography>
      </Box>

      {renderLegend()}

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
            barCategoryGap="25%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e0e0e0"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              tick={{ fill: "#8ea39a", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="period"
              tick={{ fill: "#666666", fontSize: 13 }}
              width={100}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f5f5f5" }} />
            <Bar
              dataKey="onTime"
              stackId="a"
              fill="#10b981"
              radius={[0, 0, 0, 0]}
              animationDuration={800}
            />
            <Bar
              dataKey="late"
              stackId="a"
              fill="#ef4444"
              radius={[0, 4, 4, 0]}
              animationDuration={800}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(RenewalTimeliness);
